import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Spinner } from '@klaro7/ui';

import { RegisterForm } from '@/features/auth/components/RegisterForm';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { getDefaultRedirect } from '@/features/auth/context/AuthContext';
import { useRegistrationStatus } from '@/features/settings/hooks/useServicesSettings';
import { sanitizeRedirect } from '@/features/auth/lib/sanitizeRedirect';
import { sanitizeEmailParam } from '@/features/auth/lib/sanitizeEmailParam';

export function RegisterPage() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { data: registrationStatus, isLoading: isLoadingStatus } = useRegistrationStatus();
  const [searchParams] = useSearchParams();
  const redirectTo = sanitizeRedirect(searchParams.get('redirect'));
  const initialEmail = sanitizeEmailParam(searchParams.get('email'));

  if (isLoading || isLoadingStatus) {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        style={{ background: 'linear-gradient(160deg, #231F55 0%, #1a1740 40%, #126f7d 100%)' }}
      >
        <Spinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated && user) {
    return <Navigate to={redirectTo ?? getDefaultRedirect(user.role)} replace />;
  }

  // Registrations closed: show an in-app message (the marketing landing is
  // dismessa, so we no longer redirect users off-site to it).
  if (!registrationStatus?.registrationEnabled) {
    return (
      <div
        className="dark relative flex min-h-screen items-center justify-center px-4 py-8"
        style={{ background: 'linear-gradient(160deg, #231F55 0%, #1a1740 40%, #126f7d 100%)' }}
      >
        <div className="relative z-10 w-full max-w-md text-center">
          <div className="mb-8 flex justify-center">
            <img src="/images/logo-clean.png" alt="Klaro7" className="h-14 drop-shadow-lg" />
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-8 shadow-2xl backdrop-blur-xl sm:p-10">
            <h1 className="text-2xl font-semibold tracking-tight text-white">
              Registrazioni temporaneamente chiuse
            </h1>
            <p className="mt-2 text-sm text-white/60">
              Al momento non è possibile creare nuovi account. Riprova più tardi.
            </p>
            <p className="mt-6 text-sm text-white/40">
              Hai già un account?{' '}
              <Link to="/login" className="font-medium text-brand-teal transition-colors hover:text-brand-cyan hover:underline">
                Accedi
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="dark relative flex min-h-screen items-center justify-center px-4 py-8"
      style={{
        background: 'linear-gradient(160deg, #231F55 0%, #1a1740 40%, #126f7d 100%)',
        // Spazio per il banner cookie, che è fixed e coprirebbe il bottone
        // "Registrati" su telefono.
        paddingBottom: 'calc(2rem + var(--cookie-banner-h, 0px))',
      }}
    >
      {/* Subtle radial glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 30% 20%, rgba(24,153,170,0.15) 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(118,185,185,0.08) 0%, transparent 50%)',
        }}
      />

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <img src="/images/logo-clean.png" alt="Klaro7" className="h-14 drop-shadow-lg" />
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-8 shadow-2xl backdrop-blur-xl sm:p-10">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-semibold tracking-tight text-white">
              Crea un account
            </h1>
            <p className="mt-1.5 text-sm text-white/60">
              Registrati per accedere alla piattaforma
            </p>
          </div>

          <RegisterForm redirectTo={redirectTo} initialEmail={initialEmail} />

          <p className="mt-6 text-center text-sm text-white/40">
            Hai gia un account?{' '}
            <Link to="/login" className="font-medium text-brand-teal transition-colors hover:text-brand-cyan hover:underline">
              Accedi
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
